import { useState } from "react";

const STATES = ["Vermont","New Hampshire","Maine","Connecticut","Massachusetts","New York"];
const CERTS = [
  "Family Nurse Practitioner (FNP-C) — AANP",
  "Family Nurse Practitioner (FNP-BC) — ANCC",
  "Adult-Gerontology Primary Care NP (AGPCNP-C) — AANP",
  "Adult-Gerontology Primary Care NP (AGPCNP-BC) — ANCC",
  "Pediatric NP (PNP) — PNCB or ANCC",
  "Acute Care NP (ACNP) — AACN or ANCC",
  "Other — please describe below",
];
const HOURS = ["2–5 hrs/week","5–10 hrs/week","10–20 hrs/week","20+ hrs/week"];
const AVAIL = ["Weekday mornings (before 9am)","Weekday afternoons","Weekday evenings (after 5pm)","Weekends","Flexible / anytime"];
const HOW   = ["Vermont NP Association (VANP)","NH NP Association","Maine NP Association","CT APRN Society","MA Advanced Practice Nurses","NY State NP Association","Indeed","LinkedIn","FlexJobs / NPHire","Colleague referral","Social media","Other"];

const INPUT  = "w-full border border-stone-200 rounded-lg px-4 py-3 text-stone-800 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent placeholder-stone-400 transition-all text-sm font-['Lora']";
const SELECT = INPUT + " cursor-pointer";
const LABEL  = "block text-xs font-semibold text-stone-500 uppercase tracking-widest mb-1.5 font-['Work_Sans']";
const SECTION_HEAD = "text-xl font-bold text-stone-800 mb-1 font-['Playfair_Display']";
const SECTION_SUB  = "text-sm text-stone-500 mb-5 font-['Lora']";

function Field({ label, required, children, note }) {
  return (
    <div className="mb-5">
      <label className={LABEL}>{label}{required && <span className="text-teal-600 ml-1">*</span>}</label>
      {children}
      {note && <p className="text-xs text-stone-400 mt-1.5 italic font-['Lora']">{note}</p>}
    </div>
  );
}

function CheckGroup({ options, selected, onChange, cols = 2 }) {
  return (
    <div className={`grid grid-cols-${cols} gap-2`}>
      {options.map(opt => (
        <label key={opt} onClick={() => onChange(opt)} className={`flex items-start gap-2.5 cursor-pointer group p-2.5 rounded-lg border transition-all text-sm font-['Lora'] ${
          selected.includes(opt)
            ? "border-teal-500 bg-teal-50 text-teal-800"
            : "border-stone-200 bg-white text-stone-600 hover:border-teal-300 hover:bg-stone-50"
        }`}>
          <div className={`w-4 h-4 mt-0.5 rounded flex-shrink-0 border-2 flex items-center justify-center transition-all ${
            selected.includes(opt) ? "border-teal-500 bg-teal-500" : "border-stone-300"
          }`}>
            {selected.includes(opt) && (
              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
                <path d="M10 3L5 8.5 2 5.5" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
          <span className="leading-snug">{opt}</span>
        </label>
      ))}
    </div>
  );
}

function Section({ number, title, subtitle, children }) {
  return (
    <div className="mb-10">
      <div className="flex items-center gap-3 mb-1.5">
        <div className="w-7 h-7 rounded-full bg-teal-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 font-['Work_Sans']">
          {number}
        </div>
        <h2 className={SECTION_HEAD}>{title}</h2>
      </div>
      {subtitle && <p className={SECTION_SUB + " ml-10"}>{subtitle}</p>}
      <div className="ml-10">{children}</div>
    </div>
  );
}

function ProgressBar({ step, total }) {
  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-2">
        <span className="text-xs text-stone-400 font-['Work_Sans'] uppercase tracking-widest">Application Progress</span>
        <span className="text-xs text-teal-600 font-semibold font-['Work_Sans']">{Math.round((step/total)*100)}% complete</span>
      </div>
      <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-teal-500 to-teal-400 rounded-full transition-all duration-500"
          style={{ width: `${(step/total)*100}%` }}
        />
      </div>
    </div>
  );
}

export default function NPApplication() {
  const [step, setStep]     = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm]     = useState({
    // Personal
    firstName: "", lastName: "", email: "", phone: "", city: "", homeState: "",
    // Credentials
    npi: "", certType: "", certOther: "", certExpiry: "",
    licensedStates: [], licenseNumbers: {}, licenseExpiry: {},
    deaNumber: "", hasDEA: "",
    // Experience
    yearsExperience: "", currentEmployer: "", currentRole: "",
    telehealthExp: "", lymeFamiliarity: "", multilicenseExp: "",
    // Availability
    desiredHours: "", availability: [], startDate: "", notes: "",
    // Fit
    whyTickortreat: "", howHeard: "", referralName: "",
    // Agreements
    cleanLicense: false, malpractice: false, independent: false,
  });

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const toggleState = (st) => {
    const cur = form.licensedStates;
    if (cur.includes(st)) {
      const next = cur.filter(s => s !== st);
      set("licensedStates", next);
    } else {
      set("licensedStates", [...cur, st]);
    }
  };
  const toggleArr = (field, val) => {
    const cur = form[field];
    set(field, cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val]);
  };

  const TOTAL_STEPS = 5;

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-50" style={{ fontFamily: "'Lora', serif" }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Lora:ital,wght@0,400;0,600;1,400&family=Work+Sans:wght@400;600;700&display=swap');`}</style>
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <div className="w-20 h-20 bg-teal-50 border-2 border-teal-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-stone-800 mb-3 font-['Playfair_Display']">Application Received</h1>
          <p className="text-stone-600 text-lg mb-6 font-['Lora']">
            Thank you for applying to join Tickortreat. We review all applications within 5 business days
            and will be in touch either way.
          </p>
          <div className="bg-white border border-stone-200 rounded-xl p-6 text-left mb-8">
            <p className="text-sm font-semibold text-stone-500 uppercase tracking-widest mb-3 font-['Work_Sans']">What Happens Next</p>
            {[
              ["Application review", "We\u2019ll review your credentials and experience within 5 business days."],
              ["Screening call", "If there\u2019s a good fit, we\u2019ll reach out to schedule a 15\u201320 minute conversation."],
              ["Credential verification", "License verification, NPI lookup, and malpractice confirmation."],
              ["Offer & onboarding", "Written offer, protocol training, DoseSpot setup \u2014 then you\u2019re live."],
            ].map(([step, desc], i) => (
              <div key={i} className="flex gap-3 mb-3 last:mb-0">
                <div className="w-6 h-6 rounded-full bg-teal-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5 font-['Work_Sans']">{i+1}</div>
                <div>
                  <p className="font-semibold text-stone-700 text-sm font-['Work_Sans']">{step}</p>
                  <p className="text-stone-500 text-sm font-['Lora']">{desc}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-stone-500 text-sm font-['Lora']">
            Questions? Email us at{" "}
            <a href="mailto:hello@tickortreat.com" className="text-teal-600 font-semibold hover:underline">
              hello@tickortreat.com
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" style={{ fontFamily: "'Lora', serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Lora:ital,wght@0,400;0,600;1,400&family=Work+Sans:wght@400;600;700&display=swap');
        .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        .grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
      `}</style>

      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold text-teal-600 uppercase tracking-widest font-['Work_Sans'] mb-0.5">
              Tickortreat LLC
            </div>
            <div className="text-lg font-bold text-stone-800 font-['Playfair_Display']">
              NP Application
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-stone-400 font-['Work_Sans'] uppercase tracking-widest">Step</div>
            <div className="text-2xl font-bold text-stone-700 font-['Playfair_Display']">
              {step}<span className="text-stone-300 text-lg">/{TOTAL_STEPS}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8">

        {/* Intro card — step 1 only */}
        {step === 1 && (
          <div className="bg-teal-700 rounded-2xl p-7 mb-8 text-white">
            <div className="flex items-start gap-4">
              <div className="text-4xl flex-shrink-0">🌿</div>
              <div>
                <h1 className="text-2xl font-bold mb-2 font-['Playfair_Display']">
                  Telehealth Nurse Practitioner
                </h1>
                <p className="text-teal-100 text-sm leading-relaxed font-['Lora'] mb-4">
                  Part-time · Asynchronous · $15/visit · Vermont & New England
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    ["$450/mo", "at 30 visits"],
                    ["10–15 min", "per case"],
                    ["May–Aug", "peak season"],
                  ].map(([val, label]) => (
                    <div key={val} className="bg-white/10 rounded-lg px-3 py-2 text-center">
                      <div className="text-lg font-bold font-['Playfair_Display']">{val}</div>
                      <div className="text-xs text-teal-200 font-['Work_Sans']">{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <ProgressBar step={step} total={TOTAL_STEPS} />

        <div className="bg-white rounded-2xl border border-stone-200 p-7 shadow-sm">

          {/* STEP 1 — Personal Information */}
          {step === 1 && (
            <>
              <Section number="1" title="Personal Information"
                subtitle="Basic contact details so we can reach you.">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="First Name" required>
                    <input className={INPUT} value={form.firstName}
                      onChange={e => set("firstName", e.target.value)}
                      placeholder="Jane" />
                  </Field>
                  <Field label="Last Name" required>
                    <input className={INPUT} value={form.lastName}
                      onChange={e => set("lastName", e.target.value)}
                      placeholder="Smith" />
                  </Field>
                </div>
                <Field label="Email Address" required
                  note="We'll use this as your primary contact. We don't share it.">
                  <input className={INPUT} type="email" value={form.email}
                    onChange={e => set("email", e.target.value)}
                    placeholder="jane@example.com" />
                </Field>
                <Field label="Phone Number" required>
                  <input className={INPUT} type="tel" value={form.phone}
                    onChange={e => set("phone", e.target.value)}
                    placeholder="(802) 555-0100" />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="City / Town" required>
                    <input className={INPUT} value={form.city}
                      onChange={e => set("city", e.target.value)}
                      placeholder="Burlington" />
                  </Field>
                  <Field label="State of Residence" required>
                    <select className={SELECT} value={form.homeState}
                      onChange={e => set("homeState", e.target.value)}>
                      <option value="">Select state</option>
                      {["Vermont","New Hampshire","Maine","Connecticut","Massachusetts",
                        "New York","Other"].map(s => <option key={s}>{s}</option>)}
                    </select>
                  </Field>
                </div>
              </Section>
            </>
          )}

          {/* STEP 2 — Credentials & Licensure */}
          {step === 2 && (
            <Section number="2" title="Credentials & Licensure"
              subtitle="We verify all credentials before extending an offer. Have your license numbers handy.">

              <Field label="National Provider Identifier (NPI)" required
                note="10-digit NPI number from the NPPES registry.">
                <input className={INPUT} value={form.npi}
                  onChange={e => set("npi", e.target.value)}
                  placeholder="1234567890" maxLength={10} />
              </Field>

              <Field label="NP Certification Type" required>
                <select className={SELECT} value={form.certType}
                  onChange={e => set("certType", e.target.value)}>
                  <option value="">Select certification</option>
                  {CERTS.map(c => <option key={c}>{c}</option>)}
                </select>
              </Field>

              {form.certType === "Other — please describe below" && (
                <Field label="Describe Your Certification">
                  <input className={INPUT} value={form.certOther}
                    onChange={e => set("certOther", e.target.value)}
                    placeholder="Certification name, issuing body, and year obtained" />
                </Field>
              )}

              <Field label="Certification Expiry Date" required>
                <input className={INPUT} type="month" value={form.certExpiry}
                  onChange={e => set("certExpiry", e.target.value)} />
              </Field>

              <Field label="States Where You Hold an Active NP License" required
                note="Select all that apply. We cover fees for additional licenses if you're willing to expand.">
                <div className="grid grid-cols-2 gap-2">
                  {STATES.map(st => (
                    <label key={st} onClick={() => toggleState(st)} className={`flex items-center gap-2.5 cursor-pointer p-2.5 rounded-lg border transition-all text-sm font-['Lora'] ${
                      form.licensedStates.includes(st)
                        ? "border-teal-500 bg-teal-50 text-teal-800"
                        : "border-stone-200 bg-white text-stone-600 hover:border-teal-300"
                    }`}>
                      <div className={`w-4 h-4 rounded flex-shrink-0 border-2 flex items-center justify-center ${
                        form.licensedStates.includes(st) ? "border-teal-500 bg-teal-500" : "border-stone-300"
                      }`}>
                        {form.licensedStates.includes(st) && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 12 12">
                            <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      {st}
                    </label>
                  ))}
                </div>
              </Field>

              {form.licensedStates.length > 0 && (
                <div className="bg-stone-50 rounded-xl p-4 mb-5">
                  <p className="text-xs font-semibold text-stone-500 uppercase tracking-widest mb-3 font-['Work_Sans']">
                    License Details
                  </p>
                  {form.licensedStates.map(st => (
                    <div key={st} className="grid grid-cols-2 gap-3 mb-3">
                      <Field label={`${st} — License Number`}>
                        <input className={INPUT} value={form.licenseNumbers[st] || ""}
                          onChange={e => set("licenseNumbers", { ...form.licenseNumbers, [st]: e.target.value })}
                          placeholder="License #" />
                      </Field>
                      <Field label={`${st} — Expiry`}>
                        <input className={INPUT} type="month"
                          value={form.licenseExpiry[st] || ""}
                          onChange={e => set("licenseExpiry", { ...form.licenseExpiry, [st]: e.target.value })} />
                      </Field>
                    </div>
                  ))}
                </div>
              )}

              <Field label="Do you hold a DEA registration?" required>
                <select className={SELECT} value={form.hasDEA}
                  onChange={e => set("hasDEA", e.target.value)}>
                  <option value="">Select</option>
                  <option>Yes — active DEA registration</option>
                  <option>No — I do not have a DEA number</option>
                  <option>Pending / applied</option>
                </select>
              </Field>
              {form.hasDEA === "Yes — active DEA registration" && (
                <Field label="DEA Number" note="Doxycycline is not a controlled substance — DEA is not required for this role, but helpful to have on file.">
                  <input className={INPUT} value={form.deaNumber}
                    onChange={e => set("deaNumber", e.target.value)}
                    placeholder="AB1234567" />
                </Field>
              )}
            </Section>
          )}

          {/* STEP 3 — Experience */}
          {step === 3 && (
            <Section number="3" title="Experience & Background"
              subtitle="Tell us about your clinical background. We're looking for clinical judgment and Lyme familiarity, not specific years.">

              <div className="grid grid-cols-2 gap-4">
                <Field label="Years of NP Practice" required>
                  <select className={SELECT} value={form.yearsExperience}
                    onChange={e => set("yearsExperience", e.target.value)}>
                    <option value="">Select</option>
                    {["Less than 1 year","1–2 years","3–5 years","6–10 years","10+ years"].map(y =>
                      <option key={y}>{y}</option>)}
                  </select>
                </Field>
                <Field label="Primary Practice Setting">
                  <select className={SELECT} value={form.currentRole}
                    onChange={e => set("currentRole", e.target.value)}>
                    <option value="">Select</option>
                    {["Primary care / family medicine","Urgent care","Emergency medicine",
                      "Internal medicine","Pediatrics","Telehealth (current)",
                      "Other specialty","Not currently practicing"].map(r => <option key={r}>{r}</option>)}
                  </select>
                </Field>
              </div>

              <Field label="Current or Most Recent Employer / Practice"
                note="Brief description — we're not asking you to change jobs.">
                <input className={INPUT} value={form.currentEmployer}
                  onChange={e => set("currentEmployer", e.target.value)}
                  placeholder="e.g. Community Health Center of Burlington, or Independent practice" />
              </Field>

              <Field label="Telehealth Experience" required>
                <select className={SELECT} value={form.telehealthExp}
                  onChange={e => set("telehealthExp", e.target.value)}>
                  <option value="">Select</option>
                  <option>No telehealth experience — willing to learn</option>
                  <option>Some telehealth (synchronous video visits)</option>
                  <option>Experienced in telehealth (synchronous and/or asynchronous)</option>
                  <option>Extensive telehealth background (primary modality)</option>
                </select>
              </Field>

              <Field label="Familiarity with Lyme Disease & Tick-Borne Disease" required>
                <select className={SELECT} value={form.lymeFamiliarity}
                  onChange={e => set("lymeFamiliarity", e.target.value)}>
                  <option value="">Select</option>
                  <option>Limited — I know the basics but don't see it regularly</option>
                  <option>Moderate — I see tick bites and Lyme presentations periodically</option>
                  <option>Solid — Lyme and tick-borne disease is a regular part of my practice</option>
                  <option>Extensive — I have specialized interest or training in tick-borne disease</option>
                </select>
              </Field>

              <Field label="Multi-State Licensing Experience">
                <select className={SELECT} value={form.multilicenseExp}
                  onChange={e => set("multilicenseExp", e.target.value)}>
                  <option value="">Select</option>
                  <option>No — I only hold one state license</option>
                  <option>Yes — I currently hold 2–3 state licenses</option>
                  <option>Yes — I currently hold 4+ state licenses</option>
                  <option>Previous multi-state experience, currently single-state</option>
                </select>
              </Field>
            </Section>
          )}

          {/* STEP 4 — Availability */}
          {step === 4 && (
            <Section number="4" title="Availability & Schedule"
              subtitle="We&apos;re asynchronous — you set your availability. Help us understand what works for you.">

              <Field label="Desired Weekly Hours" required>
                <div className="grid grid-cols-2 gap-2">
                  <CheckGroup
                    options={HOURS}
                    selected={[form.desiredHours]}
                    onChange={(val) => set("desiredHours", val)}
                  />
                </div>
              </Field>

              <Field label="When Are You Typically Available?" required
                note="Select all time windows that work for you. Cases will be assigned within your availability.">
                <CheckGroup
                  options={AVAIL}
                  selected={form.availability}
                  onChange={(val) => toggleArr("availability", val)}
                  cols={2}
                />
              </Field>

              <Field label="Earliest Available Start Date">
                <input className={INPUT} type="date" value={form.startDate}
                  onChange={e => set("startDate", e.target.value)} />
              </Field>

              <Field label="Anything Else About Your Schedule or Availability?">
                <textarea className={INPUT + " min-h-[80px] resize-y"} value={form.notes}
                  onChange={e => set("notes", e.target.value)}
                  placeholder="e.g. I work clinic Mon–Thu and have mornings + weekends free. Happy to do more in summer." />
              </Field>
            </Section>
          )}

          {/* STEP 5 — Fit & Agreements */}
          {step === 5 && (
            <>
              <Section number="5" title="Why Tickortreat"
                subtitle="We read every response. This doesn't need to be formal — just tell us what draws you to this.">

                <Field label="Why does this role interest you?" required
                  note="2–4 sentences is plenty. We care about genuine interest more than polished prose.">
                  <textarea className={INPUT + " min-h-[120px] resize-y"} value={form.whyTickortreat}
                    onChange={e => set("whyTickortreat", e.target.value)}
                    placeholder="e.g. I live in Vermont and see tick bites constantly in my practice. I've always wanted a way to help patients who can't get to a clinic quickly..." />
                </Field>

                <Field label="How did you hear about Tickortreat?" required>
                  <select className={SELECT} value={form.howHeard}
                    onChange={e => set("howHeard", e.target.value)}>
                    <option value="">Select</option>
                    {HOW.map(h => <option key={h}>{h}</option>)}
                  </select>
                </Field>

                {form.howHeard === "Colleague referral" && (
                  <Field label="Colleague's Name (optional)"
                    note="So we can thank them.">
                    <input className={INPUT} value={form.referralName}
                      onChange={e => set("referralName", e.target.value)}
                      placeholder="Name of colleague" />
                  </Field>
                )}
              </Section>

              {/* Attestations */}
              <div className="border border-stone-200 rounded-xl p-5 bg-stone-50">
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-widest mb-4 font-['Work_Sans']">
                  Before You Submit
                </p>
                {[
                  ["cleanLicense", "I confirm that my NP license(s) are active and in good standing with no current disciplinary actions, restrictions, or investigations."],
                  ["malpractice", "I understand that I must maintain active professional liability (malpractice) coverage as a condition of working with Tickortreat, and I currently carry or am willing to obtain such coverage."],
                  ["independent", "I understand this is a 1099 independent contractor arrangement, not an employee position, and that I am responsible for my own taxes."],
                ].map(([field, text]) => (
                  <label key={field} className={`flex items-start gap-3 cursor-pointer mb-4 last:mb-0 p-3 rounded-lg border transition-all ${
                    form[field] ? "border-teal-300 bg-teal-50/50" : "border-stone-200 bg-white hover:border-stone-300"
                  }`}>
                    <div className={`w-5 h-5 mt-0.5 rounded flex-shrink-0 border-2 flex items-center justify-center transition-all ${
                      form[field] ? "border-teal-500 bg-teal-500" : "border-stone-300"
                    }`} onClick={() => set(field, !form[field])}>
                      {form[field] && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12">
                          <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    <span className="text-sm text-stone-600 leading-relaxed font-['Lora']" onClick={() => set(field, !form[field])}>
                      {text}
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-stone-100">
            {step > 1 ? (
              <button onClick={() => setStep(s => s - 1)}
                className="flex items-center gap-2 text-stone-500 hover:text-stone-700 font-semibold text-sm transition-colors font-['Work_Sans']">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
                </svg>
                Back
              </button>
            ) : (
              <div />
            )}

            {step < TOTAL_STEPS ? (
              <button onClick={() => setStep(s => s + 1)}
                className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors text-sm font-['Work_Sans']">
                Continue
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                </svg>
              </button>
            ) : (
              <button
                onClick={async () => {
                  if (form.cleanLicense && form.malpractice && form.independent) {
                    try {
                      const response = await fetch("https://formspree.io/f/xjgjendw", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(form),
                      });
                      if (response.ok) {
                        setSubmitted(true);
                      } else {
                        alert("Submission failed — please email hello@tickortreat.com directly.");
                      }
                    } catch (error) {
                      alert("Submission failed — please email hello@tickortreat.com directly.");
                    }
                  } else {
                    alert("Please confirm all three attestations before submitting.");
                  }
                }}
                className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-7 py-3 rounded-lg transition-colors text-sm font-['Work_Sans']">
                Submit Application
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-stone-400 mt-6 font-['Lora']">
          Questions? Email{" "}
          <a href="mailto:hello@tickortreat.com" className="text-teal-600 hover:underline">
            hello@tickortreat.com
          </a>
          {" "}— we read every message.
        </p>
      </div>
    </div>
  );
}
